import { Box, Typography } from '@mui/material';

import ComposerCard from '@/karehero-app/tell-us-once/intake/ComposerCard';

/** How many lines of words stay in view; older ones go off the top. */
const LINES = 6;

/** The last few words heard are still a guess, lighter until they settle. */
const GUESS_WORDS = 3;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/**
 * What a recording is saying as it is said, in a card of its own above the
 * bar. The same card as on dev (LiveCaptionCard in ConversationalFormPage):
 * a pulsing dot and "Live captions", then the words, the latest few lighter
 * with a caret after them while it listens.
 *
 * Here the words are a script revealed as the clock runs, standing in for the
 * speech service.
 */
const LiveCaptionCard = ({
  words,
  listening,
}: {
  /** Everything heard so far. */
  words: string[];
  /** Still recording. Stopped, the dot goes grey and every word settles. */
  listening: boolean;
}) => {
  const guess = listening ? Math.min(GUESS_WORDS, words.length) : 0;
  const settled = words.slice(0, words.length - guess).join(' ');
  const guessing = words.slice(words.length - guess).join(' ');

  return (
    <ComposerCard
      title='Live captions'
      mark={
        <Box
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: listening ? 'custom.coral.600' : 'text.disabled',
            animation: listening
              ? 'liveCaptionPulse 1.6s ease-in-out infinite'
              : 'none',
            '@keyframes liveCaptionPulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(0.5)', opacity: 0.4 },
            },
            [REDUCED_MOTION]: { animation: 'none' },
          }}
        />
      }
    >
      {words.length === 0 ? (
        <Typography sx={{ color: 'text.secondary' }} variant='body1'>
          Listening…
        </Typography>
      ) : (
        // Pinned to the bottom, so the newest words are always the ones in
        // view and the oldest go off the top.
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            maxHeight: `calc(${LINES} * ${theme.typography.body1.lineHeight} * ${theme.typography.body1.fontSize})`,
            overflow: 'hidden',
          })}
        >
          <Typography
            sx={{ color: 'text.primary', overflowWrap: 'anywhere' }}
            variant='body1'
            aria-live='polite'
          >
            {settled}
            {guessing && (
              <Box
                component='span'
                sx={{ color: 'text.disabled' }}
              >{`${settled ? ' ' : ''}${guessing}`}</Box>
            )}
            {listening && (
              <Box
                component='span'
                aria-hidden
                sx={{
                  display: 'inline-block',
                  width: 2,
                  height: '1em',
                  ml: '3px',
                  verticalAlign: 'text-bottom',
                  bgcolor: 'custom.coral.600',
                  animation: 'liveCaptionCaret 1s steps(2, start) infinite',
                  '@keyframes liveCaptionCaret': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0 },
                  },
                  [REDUCED_MOTION]: { animation: 'none' },
                }}
              />
            )}
          </Typography>
        </Box>
      )}
    </ComposerCard>
  );
};

LiveCaptionCard.displayName = 'LiveCaptionCard';
export default LiveCaptionCard;
